<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import {
  COLUMN_ORDER, type CoverageBand, type CoverageCard,
} from './route-plan-coverage.utils';
import RoutePlanCoverageCard from './route-plan-coverage-card.vue';

interface Props {
  band: CoverageBand;
  readonly?: boolean;
  addingIds?: Set<number>;
  busy?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  readonly: false, addingIds: () => new Set<number>(), busy: false,
});
const emit = defineEmits<{ 'quick-add': [card: CoverageCard] }>();

const { t } = useI18n();

const COLUMN_TITLE_CLASS = {
  missing: 'text-error', inProcess: 'text-info', covered: 'text-success',
} as const;

function isAdding(card: CoverageCard): boolean {
  return card.slot ? props.addingIds.has(card.slot.weeklyPlanId) : false;
}
</script>

<template>
  <v-row dense>
    <v-col
      v-for="column in COLUMN_ORDER"
      :key="column"
      cols="12"
      md="4"
      :data-testid="`coverage-column-${column}`"
    >
      <div class="d-flex align-center mb-2">
        <span
          class="text-caption font-weight-bold text-uppercase"
          :class="COLUMN_TITLE_CLASS[column]"
        >
          {{ t(`routePlan.coverage.columns.${column}`) }} ({{ band.columns[column].length }})
        </span>
        <v-icon
          v-if="column !== 'covered'"
          icon="mdi-arrow-right-thin"
          size="small"
          class="ms-auto d-none d-md-inline text-medium-emphasis"
        />
      </div>
      <route-plan-coverage-card
        v-for="card in band.columns[column]"
        :key="card.key"
        :card="card"
        :readonly="readonly"
        :adding="isAdding(card)"
        :disabled="busy"
        @quick-add="emit('quick-add', card)"
      />
    </v-col>
  </v-row>
</template>
